import { formatDistance } from "date-fns";
import { es } from 'date-fns/locale';
import moment from "moment";
import { useCallback, useState } from 'react';
import { HiOutlineEye } from "react-icons/hi2";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { PiCurrencyDollarBold } from "react-icons/pi";
import { Link } from 'react-router-dom';
import { ActionIcon, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import SellVehicleModal from '../../../components/forms/SellVehicleModal';
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { VehicleService } from '../../../services/vehicles/vehicleService';
import { VehiclesApiResponse } from '../../../type/entities/vehicle-type';
import { vehicleStatusesSpanish } from "../../../utils/SelectorMapping";
import VehicleForm from "../components/VehicleForm";
import VehicleFilters from './VehicleFilters';

const columnOrder = [
    'id',
    'plateNumber',
    'company',
    'brand',
    'model',
    'status',
    'updatedAt',
    'purchaseDate',
    'saleDate',
    'registrationDate',
    'wildcard',
    'power',
    'cylinderCapacity',
    'fuelType',
    'owner'
];

interface VehiclesListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    vehicleSelected: any;
    setVehicleSelected: React.Dispatch<React.SetStateAction<any>>;
}

const VehiclesList: React.FC<VehiclesListProps> = ({ openFormDrawer, setOpenFormDrawer, vehicleSelected, setVehicleSelected }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [sellLoading, setSellLoading] = useState<Record<string, boolean>>({});
    const [sellModalOpen, setSellModalOpen] = useState(false);
    const [selectedVehicleForSale, setSelectedVehicleForSale] = useState<any>(null);

    const service = new VehicleService();

    // HOOKS
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE VEHÍCULOS
     * @EN GETS THE LIST OF VEHICLES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listVehicles(filters);
        return response.getResponseData() as VehiclesApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL CAMBIO DE ESTADO DEL VEHÍCULO
     * @EN HANDLES THE VEHICLE STATE CHANGE
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleVehicleStateChange = async (entityId: string, statusId: string, comment: string) => {
        try {
            setChangingStatus([...changingStatus, entityId]);
            // Convertir statusId a boolean para usar con toggleVehicleStatus
            const isActive = statusId === 'active' || statusId === '1';
            const response = (await service.toggleVehicleStatus(entityId, isActive)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== entityId));
                refetch();
                toast.success("Estado del vehículo actualizado correctamente");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== entityId));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== entityId));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACIÓN DE UN VEHÍCULO
     * @EN HANDLES THE DELETION OF A VEHICLE
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteVehicle(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Vehículo eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACIÓN DE VARIOS VEHÍCULOS
     * @EN HANDLES THE DELETION OF MULTIPLE VEHICLES
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiVehicles(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Vehículos eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ABRE EL MODAL PARA VENDER UN VEHÍCULO
     * @EN OPENS THE MODAL TO SELL A VEHICLE
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleOpenSellModal = (vehicle: any) => {
        setSelectedVehicleForSale(vehicle);
        setSellModalOpen(true);
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA VENTA DE UN VEHÍCULO
     * @EN HANDLES THE SALE OF A VEHICLE
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleSellVehicle = async (saleDate: Date) => {
        if (!selectedVehicleForSale) return;

        const vehicleId = selectedVehicleForSale.id;
        try {
            setSellLoading(prev => ({ ...prev, [vehicleId]: true }));
            // Format saleDate to this format 'dd-mm-yyyy'
            const formattedDate = moment(saleDate).format('YYYY-MM-DD');
            const response = await (await service.markAsSold(vehicleId, formattedDate)).getResponseData();
            if (response.success) {
                setSellLoading(prev => ({ ...prev, [vehicleId]: false }));
                setSellModalOpen(false);
                setSelectedVehicleForSale(null);
                refetch();
                toast.success('Vehículo marcado como vendido correctamente');
            } else {
                setSellLoading(prev => ({ ...prev, [vehicleId]: false }));
                handleErrors(response);
            }
        } catch (error) {
            setSellLoading(prev => ({ ...prev, [vehicleId]: false }));
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES CIERRA EL MODAL DE VENTA
     * @EN CLOSES THE SELL MODAL
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCloseSellModal = () => {
        setSellModalOpen(false);
        setSelectedVehicleForSale(null);
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES FORMATEA EL NOMBRE DE LA COLUMNA
     * @EN FORMATS THE COLUMN NAME
     */
    //-------------------------------------------------------------------------------------------------------------
    const capitalize = (str: string) => {
        if (typeof str !== 'string') return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'vehicles-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    isSortable
                    columnsNoSortables={['active', 'status']}
                    columnsNotShown={['updatedAt', 'registrationDate','wildcard','power','cylinderCapacity','fuelType','owner']}
                    overrideColumns={[
                        {
                            key: 'plateNumber',
                            columnNameKey: 'Matrícula',
                            render: (row: any) => {
                                const value = row.row.original.plateNumber;
                                return (
                                    <a href={`/vehicles/${row.row.original.id}/profile/info`} className='text-primary hover:text-primary hover:underline'>
                                        {value}
                                    </a>
                                );
                            }
                        },
                        {
                            key: 'company',
                            columnNameKey: 'Compañía',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                return row.row.original.company?.name || 'N/A';
                            },
                        },
                        {
                            key: 'status',
                            columnNameKey: 'Estado',
                            render: (row: any) => {
                                const status = row.row.original.status;
                                const statusOption = vehicleStatusesSpanish.find((item) => item.value === status);

                                if (!status) return 'Sin estado';
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center' key={row.row.original.id + status}>
                                        <StatusDropdown
                                            entityId={row.row.original?.id}
                                            title="¿Estas seguro de cambiar el estado?"
                                            statesOptions={vehicleStatusesSpanish.filter((item) => item.value !== 'sold')}
                                            currentState={{
                                                name: statusOption?.label,
                                                id: statusOption?.value,
                                                color: statusOption?.color
                                            }}
                                            handleStateChange={(entityId: string, statusId: string, comment: string) => handleVehicleStateChange(entityId, statusId, comment)}
                                            disabled={!userCan('edit', 'vehicle') || status === 'sold'}
                                        />
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'purchaseDate',
                            columnNameKey: 'Fecha de compra',
                            render: (row: any) => {
                                const date = row.row.original?.purchaseDate?.date;
                                return date ? moment(date).format('DD-MM-YYYY') : 'Sin Fecha';
                            },
                        },
                        {
                            key: 'saleDate',
                            columnNameKey: 'Fecha de venta',
                            render: (row: any) => {
                                const date = row.row.original?.saleDate?.date;
                                return date ? moment(date).format('DD-MM-YYYY') : 'Sin Fecha';
                            },
                        },
                        {
                            key: 'updatedAt',
                            columnNameKey: 'Última actualización',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'vehicle', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`/vehicles/${row.id}/profile/info`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'vehicle', permission: 'edit' },
                            hide: (row: any) => row.status === 'sold',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar vehículo'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setVehicleSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Sell',
                            permissions: { group: 'vehicle', permission: 'edit' },
                            hide: (row: any) => row.status === 'sold',
                            render: (row: any) => {
                                const isLoading = sellLoading[row.id] || false;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Marcar como vendido'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => handleOpenSellModal(row)}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-green-600 hover:text-green-600 bg-white"
                                            disabled={isLoading}
                                        >
                                            {isLoading ? (
                                                <div className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full" />
                                            ) : (
                                                <PiCurrencyDollarBold className="h-4 w-4" size={20} />
                                            )}
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'vehicle', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar vehículo`}
                                                description={`¿Estás seguro de que deseas eliminar el vehículo ${row.plateNumber}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as VehiclesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Vehículos'} onClear={() => resetFilters(50)}>
                <VehicleFilters />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    vehicleSelected ?
                        "Completa el formulario para editar los datos del vehículo" :
                        "Completa el formulario para crear un nuevo vehículo en el sistema"
                }
                drawerTitle={vehicleSelected ? 'Editar Vehículo' : 'Crear Vehículo'}
                submitText={vehicleSelected ? 'Guardar Vehículo' : 'Crear Vehículo'}>
                <VehicleForm setOpenModal={setOpenFormDrawer} vehicle={vehicleSelected} refetch={refetch} />
            </FormDrawerView>

            <SellVehicleModal
                isOpen={sellModalOpen}
                onClose={handleCloseSellModal}
                onConfirm={handleSellVehicle}
                vehicle={selectedVehicleForSale ?? null}
                isLoading={selectedVehicleForSale ? sellLoading[selectedVehicleForSale.id] || false : false}
            />
        </Page>
    );
};

export default VehiclesList;